import React, { useState } from "react";
import { toast } from "sonner";
import { ScanLine, Sparkles } from "lucide-react";
import "./v19shared.css";
import "./v20extra.css";

const TEMPLATES = [
  { name: "Internal Medicine — Standard", spec: "Internal Medicine", codes: ["99213", "99214", "+6"], req: "DOS · Provider · POS · Dx", edited: "2d ago", st: ["st-g", "Published"], act: ["ghost", "Edit", "Opening template editor…"] },
  { name: "Annual Wellness Visit", spec: "Primary Care", codes: ["G0438", "G0439"], req: "DOS · Provider · Risk Z-code", edited: "6d ago", st: ["st-g", "Published"], act: ["ghost", "Edit", "Opening template editor…"] },
  { name: "Orthopedics — Procedure", spec: "Orthopedics", codes: ["20610", "29881", "+4"], req: "DOS · Provider · Modifier · Site", edited: "9d ago", st: ["st-g", "Published"], act: ["ghost", "Edit", "Opening template editor…"] },
  { name: "Behavioral Health — 60 min", spec: "Behavioral Health", codes: ["90837", "90791"], req: "DOS · Provider · Start/stop time", edited: "1d ago", st: ["st-a", "Draft"], act: ["", "Finish", "Finish required fields to publish"] },
  { name: "Physical Therapy — Visit", spec: "PT / Rehab", codes: ["97110", "97140", "+3"], req: "DOS · Provider · Units · Auth #", edited: "4h ago", st: ["st-x", "From Lens · review"], act: ["", "Review", "Reviewing Lens-built template…"] },
];
const PROFILES = [
  { plat: "athenahealth", acct: "Queens Medical Assoc.", prof: "Athena → RevQ v3", map: ["st-g", "12 / 12 mapped"], stat: ["st-g", "Connected"], last: "15d ago", act: ["ghost", "Sync", "Sync started — pulling new encounters"] },
  { plat: "DrChrono", acct: "Queens Medical Assoc.", prof: "DrChrono → RevQ v2", map: ["st-a", "8 / 9 mapped"], stat: ["st-a", "1 provider unmapped"], last: "3d ago", act: ["", "Fix mapping", "Open mapping — assign the unmapped provider"] },
  { plat: "Paper / scanned", acct: "Queens Medical Assoc.", prof: "Lens auto-map", map: ["st-g", "Auto"], stat: ["st-g", "Active"], last: "4h ago", act: ["ghost", "Scan", "Opening Lens · scan superbill…"] },
];
const JOBS = [
  { id: "SBX-2026-041", src: "athenahealth", tmpl: "Internal Medicine", pts: "38", claims: "38", exc: <span className="ok-dim">0</span>, st: ["st-g", "Complete"], act: ["ghost", "View claims", "Opening generated claims…"] },
  { id: "SBX-2026-042", src: "DrChrono", tmpl: "Orthopedics", pts: "14", claims: "12", exc: <span style={{ color: "var(--amber-deep)", fontWeight: 600 }}>2</span>, st: ["st-a", "Needs review"], act: ["", "Review exceptions", "Opening exception worklist…"] },
  { id: "SBX-2026-043", src: "Paper / Lens", tmpl: "PT — Visit", pts: "9", claims: "9", exc: <span className="ok-dim">0</span>, st: ["st-g", "Complete"], act: ["ghost", "View claims", "Opening generated claims…"] },
  { id: "SBX-2026-044", src: "athenahealth", tmpl: "Annual Wellness", pts: "22", claims: "—", exc: "—", st: ["st-b", "Running…"], act: ["ghost", "Monitor", "Job 64% complete…"] },
];
const ENCOUNTERS = [
  { mrn: "100022", pt: "James Park", ph: "Dr. Wilson", enc: "47", dos: "06/10/2026", type: "ASA", icd: 0, cpt: 0 },
  { mrn: "100038", pt: "Sarah Johnson", ph: "Dr. Chen", enc: "48", dos: "06/10/2026", type: "Annual Physical", icd: 0, cpt: 0 },
  { mrn: "100022", pt: "Michael Torres", ph: "Dr. Roberts", enc: "49", dos: "06/09/2026", type: "Follow-up Visit", icd: 1, cpt: 0 },
];
const HISTORY = [
  { pt: "Eleanor Vance", prior: "6", claims: "6", changes: "1 · E/M upcode", denials: "0", issue: <span style={{ color: "var(--gray3)" }}>None</span>, act: ["ghost", "Timeline", "Opening patient timeline…"] },
  { pt: "James Thompson", prior: "4", claims: "4", changes: "2 · modifier 25", denials: "1", issue: <span style={{ color: "var(--amber-deep)", fontWeight: 600 }}>Recurring mod-25 strip</span>, act: ["", "See pattern", "Opening denial pattern…"] },
  { pt: "Sandra Lee", prior: "9", claims: "9", changes: "0", denials: "0", issue: <span style={{ color: "var(--gray3)" }}>None</span>, act: ["ghost", "Timeline", "Opening patient timeline…"] },
  { pt: "Robert Miller", prior: "3", claims: "2", changes: "1 · added 36415", denials: "2", issue: <span style={{ color: "var(--red-deep)", fontWeight: 600 }}>Missing dx → denials</span>, act: ["", "Investigate", "Opening denial investigation…"] },
];

const scan = () => toast.message("Opening Lens · scan a superbill…");

export default function Superbills() {
  const [tab, setTab] = useState("tmpl");
  const T = (id, label, ct) => (
    <div className={`seg-opt${tab === id ? " on" : ""}`} onClick={() => setTab(id)} data-testid={`sb-tab-${id}`}>{label}{ct != null ? <span className="seg-ct">{ct}</span> : null}</div>
  );

  return (
    <div data-testid="superbills-view" className="flex flex-col gap-[14px]">
      <div className="v19">
        <div className="view on">
          <div className="pg-row">
            <div><div className="pg-title">Superbills</div><div className="pg-sub">Templates, platform mappings, execution jobs, and patient history — charge capture as a production workflow</div></div>
            <div className="seg seg-underline">
              {T("tmpl", "Templates", 5)}
              {T("prof", "Platform Profiles", 3)}
              {T("jobs", "Execution Jobs", 4)}
              {T("code", "Code / Capture")}
              {T("hist", "Patient History")}
            </div>
          </div>

          <div className="lens-banner">
            <div className="lens-ico"><ScanLine size={20} /></div>
            <div>
              <div className="lens-title">Build a template by scanning a real superbill with <em>Lens</em></div>
              <div className="lens-sub">Drop one paper superbill or encounter form — Lens detects the specialty, maps the CPT/ICD grid, and proposes required fields. You review, name it, and it's ready to execute against incoming charges.</div>
            </div>
            <button className="btn-lens" onClick={scan} data-testid="sb-scan">Scan a superbill</button>
          </div>

          {/* Templates */}
          {tab === "tmpl" && (
            <div className="segpane on" data-testid="sb-pane-tmpl">
              <div className="panel glass">
                <div className="panel-hdr"><div className="panel-title">Templates <span className="count-pill">5</span></div><button className="row-act" onClick={scan}>+ New template</button></div>
                <table className="tbl"><tbody>
                  <tr><th>Template</th><th>Specialty</th><th>Default CPT / ICD</th><th>Required fields</th><th>Last edited</th><th>Status</th><th /></tr>
                  {TEMPLATES.map((t) => (
                    <tr key={t.name}><td className="b">{t.name}</td><td>{t.spec}</td>
                      <td><span className="code-pills">{t.codes.map((c) => <span key={c} className="code-pill">{c}</span>)}</span></td>
                      <td>{t.req}</td><td>{t.edited}</td><td><span className={`st ${t.st[0]}`}>{t.st[1]}</span></td>
                      <td><button className={`row-act ${t.act[0]}`} onClick={() => toast.message(t.act[2])}>{t.act[1]}</button></td></tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}

          {/* Platform Profiles */}
          {tab === "prof" && (
            <div className="segpane on" data-testid="sb-pane-prof">
              <div className="panel glass">
                <div className="panel-hdr"><div className="panel-title">Platform profiles <span className="count-pill">3</span></div><span style={{ fontSize: 9.5, color: "var(--gray3)" }}>Where superbills come from, and how they map into RevQ</span></div>
                <table className="tbl"><tbody>
                  <tr><th>Source platform</th><th>Client / account</th><th>Mapping profile</th><th>Provider / location mapping</th><th>Status</th><th>Last import</th><th /></tr>
                  {PROFILES.map((p) => (
                    <tr key={p.plat}><td className="b">{p.plat}</td><td>{p.acct}</td><td>{p.prof}</td>
                      <td><span className={`st ${p.map[0]}`}>{p.map[1]}</span></td><td><span className={`st ${p.stat[0]}`}>{p.stat[1]}</span></td><td>{p.last}</td>
                      <td><button className={`row-act ${p.act[0]}`} onClick={() => toast.message(p.act[2])}>{p.act[1]}</button></td></tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}

          {/* Execution Jobs */}
          {tab === "jobs" && (
            <div className="segpane on" data-testid="sb-pane-jobs">
              <div className="panel glass">
                <div className="panel-hdr"><div className="panel-title">Execution jobs <span className="count-pill">4</span></div><span style={{ fontSize: 9.5, color: "var(--gray3)" }}>Each job turns source charges into claims</span></div>
                <table className="tbl"><tbody>
                  <tr><th>Job ID</th><th>Source</th><th>Template</th><th>Patients</th><th>Claims created</th><th>Exceptions</th><th>Status</th><th /></tr>
                  {JOBS.map((j) => (
                    <tr key={j.id}><td className="mono">{j.id}</td><td>{j.src}</td><td>{j.tmpl}</td><td>{j.pts}</td><td>{j.claims}</td><td>{j.exc}</td>
                      <td><span className={`st ${j.st[0]}`}>{j.st[1]}</span></td>
                      <td><button className={`row-act ${j.act[0]}`} onClick={() => toast.message(j.act[2])}>{j.act[1]}</button></td></tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}

          {/* Code / Capture */}
          {tab === "code" && (
            <div className="segpane on" data-testid="sb-pane-code">
              <div className="lens-banner" style={{ background: "rgba(0,0,0,.025)", border: "1px solid var(--glass-border-soft)", color: "var(--ink)" }}>
                <div className="lens-ico" style={{ background: "rgba(0,0,0,.06)", color: "var(--ink)" }}><Sparkles size={20} /></div>
                <div>
                  <div className="lens-title" style={{ color: "var(--ink)" }}>Manual code capture — the superbill is the <em>source receipt of services</em></div>
                  <div className="lens-sub" style={{ color: "var(--gray2)" }}>Build the receipt by hand: pick CPTs and diagnoses for an encounter. This is the familiar path for coders coming from Enable. <b>Generate Claim Data</b> (AI) is the upgrade — it pre-fills these codes with confidence + evidence, and you can always drop back here to override.</div>
                </div>
                <button className="btn-lens" onClick={() => toast.message("Opening AI coding workspace…")} data-testid="sb-generate-ai"><Sparkles size={13} /> Generate with AI</button>
              </div>
              <div className="panel glass">
                <div className="panel-hdr"><div className="panel-title">Pick an encounter to code <span className="count-pill">from Not-Generated queue</span></div><span className="panel-link" onClick={() => toast.message("Open Create Claims queue →")}>Open Create Claims queue →</span></div>
                <table className="tbl"><tbody>
                  <tr><th>MRN</th><th>Patient</th><th>Physician</th><th>Enc. #</th><th>Enc. Date</th><th>Type of Visit</th><th>ICD</th><th>CPT</th><th /></tr>
                  {ENCOUNTERS.map((e, i) => (
                    <tr key={i} className="cq-row" onClick={() => toast.message(`Opening Code / Capture · ${e.pt}`)} data-testid={`sb-enc-${i}`}>
                      <td>{e.mrn}</td><td className="b">{e.pt}</td><td>{e.ph}</td><td>{e.enc}</td><td>{e.dos}</td>
                      <td><span className="vt">{e.type}</span></td>
                      <td><span className={`rdy ${e.icd ? "rdy-ok" : "rdy-no"}`}>{e.icd ? "✓" : "✗"}</span></td>
                      <td><span className={`rdy ${e.cpt ? "rdy-ok" : "rdy-no"}`}>{e.cpt ? "✓" : "✗"}</span></td>
                      <td><button className="row-act" onClick={(ev) => { ev.stopPropagation(); toast.message(`Code / Capture · ${e.pt}`); }}>Code / Capture</button></td>
                    </tr>
                  ))}
                </tbody></table>
                <div style={{ fontSize: 9.5, color: "var(--gray3)", padding: "9px 2px 0" }}>Superbill (receipt) → AI coding workspace (analysis) → Claim (submission). Click any encounter to open its split CPT / ICD picker.</div>
              </div>
            </div>
          )}

          {/* Patient History */}
          {tab === "hist" && (
            <div className="segpane on" data-testid="sb-pane-hist">
              <div className="panel glass">
                <div className="panel-hdr"><div className="panel-title">Patient superbill history <span className="count-pill">repeat-issue radar</span></div><span style={{ fontSize: 9.5, color: "var(--gray3)" }}>ShAWL flags patterns across a patient's superbills</span></div>
                <table className="tbl"><tbody>
                  <tr><th>Patient</th><th>Prior superbills</th><th>Claims generated</th><th>Coding changes</th><th>Related denials</th><th>Repeated issue</th><th /></tr>
                  {HISTORY.map((h) => (
                    <tr key={h.pt}><td className="b">{h.pt}</td><td>{h.prior}</td><td>{h.claims}</td><td>{h.changes}</td><td>{h.denials}</td><td>{h.issue}</td>
                      <td><button className={`row-act ${h.act[0]}`} onClick={() => toast.message(h.act[2])}>{h.act[1]}</button></td></tr>
                  ))}
                </tbody></table>
              </div>
            </div>
          )}

          <div className="v20-devnote" style={{ marginTop: 4 }}><span><b>Dev note</b> — Templates, platform profiles, execution jobs, coding queue and patient history are mocked sample data — wire to the live superbill template store + EHR mapping profiles when available.</span></div>
        </div>
      </div>
    </div>
  );
}
